define('presentation/receptionist/viewModels/receptionistViewModel',[
    'businessServices/contacts/contactsStateSingleton',
    'businessServices/events/eventManager',
    'common/collections/collectionFilter',
    'common/converters/phoneNumberFormatter',
    'common/promises/promiseFactory',
    'common/storage/commonState',
    'common/time/datetimeFormatter',
    'constants/liveReceptionistScriptTypeConstants',
    'constants/receptionistMessageConstants',
    'constants/scrollEventSources',
    'constants/systemObjectIconNameConstants',
    'presentation/receptionist/dataSources/messageSubscriptionDataSource',
    'presentation/receptionist/facades/receptionistFacade',
    'presentation/common/window/windowControl',
    'settings/navigationConfiguration'
], function () {
    const DateTimeFormatterConstructor = require('common/time/datetimeFormatter');
    const _dateTimeFormatter = new DateTimeFormatterConstructor();

    const PhoneNumberFormatterConstructor = require('common/converters/phoneNumberFormatter');
    const _phoneNumberFormatter = new PhoneNumberFormatterConstructor();

    const PromiseFactoryConstructor = require('common/promises/promiseFactory');
    const _promiseFactory = new PromiseFactoryConstructor();

    const FilterConstructor = require('common/collections/collectionFilter');

    const _receptionistMessageConstants = require('constants/receptionistMessageConstants');
    const _commonState = require('common/storage/commonState');
    const _contactsState = require('businessServices/contacts/contactsStateSingleton');
    const _eventManager = require('businessServices/events/eventManager');
    const _liveReceptionistScriptTypeConstants = require('constants/liveReceptionistScriptTypeConstants');
    const _systemObjectIconNames = require('constants/systemObjectIconNameConstants');
    const ScrollEventSources = require('constants/scrollEventSources');

    const _messageSubscriptionDataSource = require('presentation/receptionist/dataSources/messageSubscriptionDataSource');
    let _availableSubscriptions = _messageSubscriptionDataSource.subscriptions;

    let _facade = null;
    let _navigationConfiguration = null;
    let _receptionistMessageContentPaneScrollEventId = null;
    let _windowControl = null;

    let isScreenAlreadyInitialized = false;
    let isListInitialized = false;
    let sidebar = null;
    let spinEndTime = null;

    const MINIMUM_ITEM_COUNT = 10;
    const _state = {
        pageNumber: 0,
        numberOfReceptionistMessagesShown: 0,
        availableReceptionistMessages: [],
        isLastPage: false,
        PAGE_ITEM_COUNT: 150,
        ITEMS_PER_PAGE_DISPLAYED: 20
    };

    const parentViewModel = ko.observable();

    const scrollEventSource = ScrollEventSources.receptionistMessages;

    const debounceEvent = (callback, time) => {
        let interval;
        return (...args) => {
            clearTimeout(interval);
            interval = setTimeout(() => {
                interval = null;
                callback(...args);}, time);
        };
    };

    const formatPhoneNumber = (phoneNumber) => {
        return _phoneNumberFormatter.toInternationalWithParens(phoneNumber);
    };

    const isDataLoading = ko.observable(true);
    const showEmptyState = ko.computed(() => {
        if (isDataLoading() === false) {
            if (receptionistMessages().length === 0) {
                return true;
            }
        }
        return false;
    });

    const anyReceptionistMessages = ko.observable(true);
    const emptyStateContentKey = ko.pureComputed(() => anyReceptionistMessages() ?
        "receptionist:noResults" :
        "receptionist:noReceptionistMessages");
    const receptionistMessages = ko.observableArray([]);

    const prepareReceptionistMessageForDisplay = (originalReceptionistMessage) => {
        let receptionistMessageJunkStatus = ko.observable('');
        switch (originalReceptionistMessage.junkStatus) {
            case _receptionistMessageConstants.junkStatus.abandoned.id:
                receptionistMessageJunkStatus(_receptionistMessageConstants.junkStatus.abandoned.name);
                break;
            case _receptionistMessageConstants.junkStatus.hangup.id:
                receptionistMessageJunkStatus(_receptionistMessageConstants.junkStatus.hangup.name);
                break;
            case _receptionistMessageConstants.junkStatus.spam.id:
                receptionistMessageJunkStatus(_receptionistMessageConstants.junkStatus.spam.name);
                break;
            case _receptionistMessageConstants.junkStatus.externalWebsite.id:
                receptionistMessageJunkStatus(_receptionistMessageConstants.junkStatus.externalWebsite.name);
                break;
        }
        let receptionistMessageStatus = ko.observable(_receptionistMessageConstants.status.new.name);
        switch (originalReceptionistMessage.status) {
            case _receptionistMessageConstants.status.new.id:
                receptionistMessageStatus(_receptionistMessageConstants.status.new.name);
                break;
            case _receptionistMessageConstants.status.reviewed.id:
                receptionistMessageStatus(_receptionistMessageConstants.status.reviewed.name);
                break;
        }
        let isReceptionistMessageStatusNew = ko.pureComputed(() => {
            return (receptionistMessageStatus() === _receptionistMessageConstants.status.new.name);
        });
        let statusI18nKey = ko.pureComputed(() => {
            return {key: 'isNew', options: {context: receptionistMessageStatus().toLowerCase()}};
        });

        let scriptTypeIconName = null;
        switch (originalReceptionistMessage.liveReceptionistScriptType) {
            case _liveReceptionistScriptTypeConstants.LiveReceptionist:
                scriptTypeIconName = _systemObjectIconNames.hostedNumber;
                break;
            case _liveReceptionistScriptTypeConstants.WebChat:
                scriptTypeIconName = _systemObjectIconNames.webChatWidget;
                break;
            case _liveReceptionistScriptTypeConstants.OutboundCalling:
                scriptTypeIconName = _systemObjectIconNames.outboundCallingCampaign;
                break;
        }

        let originatorAvatar = null;
        let originatorIconName = null;
        let originatorInitials = null;
        let originatorType = 'external';
        let originatorTitle;
        let originatorMetadata = null;

        let liveReceptionistScriptMetadata = null;

        let hostedPhoneNumberFormatted = null;
        let hostedPhoneNumberRaw = null;

        switch (originalReceptionistMessage.interactionType) {
            case _receptionistMessageConstants.interactionType.chat.id:
                originatorIconName = _systemObjectIconNames.webChatWidget;
                originatorTitle = originalReceptionistMessage.participantName;
                originatorMetadata = originalReceptionistMessage.participantEmailAddress;
                liveReceptionistScriptMetadata = originalReceptionistMessage.widgetDisplayName;
                break;
            default:
                originatorIconName = _systemObjectIconNames.hostedNumber;
                const contact = _contactsState.getContactByPhoneNumber(originalReceptionistMessage.callerAni);
                if (contact) {
                    originatorAvatar = contact.avatarUrl;
                    originatorTitle = contact.displayName;
                    originatorInitials = contact.initials;
                    originatorType = _commonState.types.contact;

                    const matchingNumber = contact.phoneNumbers().find(x => _phoneNumberFormatter.toEOneSixFour(x.rawPhoneNumber()) ===  _phoneNumberFormatter.toEOneSixFour(originalReceptionistMessage.callerAni));
                    originatorMetadata = matchingNumber ? matchingNumber.label : null;
                } else if (originalReceptionistMessage.callerAni) {
                    originatorTitle = formatPhoneNumber(originalReceptionistMessage.callerAni);
                    originatorMetadata = _phoneNumberFormatter.buildLocation(originalReceptionistMessage.callerAni, originalReceptionistMessage.callerLocationCity, originalReceptionistMessage.callerLocationState);
                }

                let hostedNumber = _commonState.hostedNumbers().find((hostedNumber) => {
                    return hostedNumber.id === originalReceptionistMessage.accountHostedNumberId;
                });

                if (hostedNumber) {
                    hostedPhoneNumberFormatted = hostedNumber.phoneNumber();
                    hostedPhoneNumberRaw = _phoneNumberFormatter.clean(hostedNumber.phoneNumber());
                    liveReceptionistScriptMetadata = hostedNumber.name;
                }
        }

        if (originalReceptionistMessage.isJunk) {
            originatorTitle = receptionistMessageJunkStatus();
            originatorIconName = 'actionDelete';
        }

        return {
            receptionistMessageId: originalReceptionistMessage.receptionistMessageId,

            status: receptionistMessageStatus,
            isStatusNew: isReceptionistMessageStatusNew,

            junkStatus: originalReceptionistMessage.junkStatus,
            isJunk: originalReceptionistMessage.isJunk,

            originatorAvatar: originatorAvatar,
            originatorIconName: originatorIconName,
            originatorInitials: originatorInitials,
            originatorType: originatorType,
            originatorTitle: originatorTitle,
            originatorMetadata: originatorMetadata,
            callerAni: originalReceptionistMessage.callerAni,
            callHistoryId: originalReceptionistMessage.callHistoryId,

            hostedNumberId: originalReceptionistMessage.accountHostedNumberId,
            hostedPhoneNumber: hostedPhoneNumberRaw,
            hostedPhoneNumberFormatted: hostedPhoneNumberFormatted,
            liveReceptionistScriptMetadata: liveReceptionistScriptMetadata,

            liveReceptionistScriptId: originalReceptionistMessage.liveReceptionistScriptId,
            liveReceptionistScriptName: originalReceptionistMessage.liveReceptionistScriptDisplayName,
            liveReceptionistScriptTypeIconName: scriptTypeIconName,
            messageLeftDateTime: originalReceptionistMessage.createdDateTime,
            date: _dateTimeFormatter.humanizedDay(originalReceptionistMessage.createdDateTime),

            isExpanded: ko.observable(false),
            isReady: ko.observable(false),
            isStatusChanging: ko.observable(true),
            statusI18nKey: statusI18nKey,

            callDetails: ko.observable(null),
            formResponses: ko.observable(null)
        };
    };

    const removeReceptionistMessageFromUi = (receptionistMessageId) => {
        receptionistMessages.remove((item) => {
            return item.receptionistMessageId === receptionistMessageId;
        });
    };

    const setReceptionistMessageUiStatus = (receptionistMessageId, newStatus) => {
        let allReceptionistMessages = receptionistMessages();
        let receptionistMessage = allReceptionistMessages.find((m) => {
            return m.receptionistMessageId === receptionistMessageId;
        });

        if (receptionistMessage) {
            receptionistMessage.status(newStatus);
            receptionistMessage.isStatusChanging(false);
        }
    };

    const setReceptionistMessageStatus = (receptionistMessage, newStatus) => {
        return _promiseFactory.defer((promise) => {
            if (receptionistMessage.status() === newStatus) {
                receptionistMessage.isStatusChanging(false);
                promise.resolve();
                return;
            }
            receptionistMessage.isStatusChanging(true);

            _facade.setReceptionistMessageStatus(receptionistMessage.receptionistMessageId, newStatus)
                .fail(promise.reject)
                .done(() => {
                    setReceptionistMessageUiStatus(receptionistMessage.receptionistMessageId, newStatus);
                    promise.resolve();
                });
        });
    };

    const onReceptionistMessageStatusChanged = (receptionistMessageId, newStatus) => {
        let actualStatus = _receptionistMessageConstants.status.new.name;
        switch (newStatus) {
            case _receptionistMessageConstants.status.new.id:
                actualStatus = _receptionistMessageConstants.status.new.name;
                break;
            case _receptionistMessageConstants.status.reviewed.id:
                actualStatus = _receptionistMessageConstants.status.reviewed.name;
                break;
        }

        setReceptionistMessageUiStatus(receptionistMessageId, actualStatus);
    };

    const onReceptionistMessageAdded = (receptionistMessageId) => {
        const isMessageNotAlreadyPresent = receptionistMessages().every(m => m.receptionistMessageId !== receptionistMessageId);
        if (isMessageNotAlreadyPresent) {
            const searchOptions = sidebar.getSearchOptions();

            _facade.getFilteredReceptionistMessage(receptionistMessageId, searchOptions)
                .done(({ hasReceptionistMessage, foundReceptionistMessage }) => {
                    if (hasReceptionistMessage) {
                        const preparedReceptionistMessage = prepareReceptionistMessageForDisplay(foundReceptionistMessage, _availableSubscriptions());
                        const { searchText } = searchOptions;
                        const messageExistsInFilter = searchFilterReceptionistMessages(searchText, [preparedReceptionistMessage]).length > 0;
                        if (messageExistsInFilter) {
                            receptionistMessages.unshift(preparedReceptionistMessage);
                        }
                    }
                });
        }
    };

    const onSubscriberSettingsChanged = (eventData) => {
        const { liveReceptionistScriptId, isActiveSubscriber, isPushToWebAppEnabled } = eventData;
        const areLiveReceptionistScriptMessagesPresent = receptionistMessages().some(message => message.liveReceptionistScriptId === liveReceptionistScriptId);
        if (isActiveSubscriber === true)
        {
            if (isPushToWebAppEnabled === true) {
                if (areLiveReceptionistScriptMessagesPresent === false) {
                    refreshMessageListing();
                }
            }
            else
            {
                if (areLiveReceptionistScriptMessagesPresent === true) {
                    refreshMessageListing();
                }
            }
        }
        else
        {
            if (areLiveReceptionistScriptMessagesPresent === true) {
                refreshMessageListing();
            }
        }
    };

    const onLiveReceptionistScriptStatusChanged = (eventData) => {
        const { liveReceptionistScriptId } = eventData;

        const areLiveReceptionistScriptMessagesPresent = receptionistMessages().some(message => message.liveReceptionistScriptId === liveReceptionistScriptId);
        if (areLiveReceptionistScriptMessagesPresent === false) {
            refreshMessageListing();
        }
    };

    const onReceptionistMessageDeleted = (receptionistMessageId) => {
        removeReceptionistMessageFromUi(receptionistMessageId);
    };

    const onSearchTextChanged = () => {
        refreshMessageListing();
    };

    const refreshMessageListing = () => {
        return _promiseFactory.defer((promise) => {
            spinEndTime = 1500 + new Date().getTime();
            isDataLoading(true);

            _state.pageNumber = 0;

            if (sidebar === null) {
                promise.resolve();
                return;
            }

            const searchOptions = sidebar.getSearchOptions();
            const { searchText } = searchOptions;
            const options = Object.assign({}, searchOptions, {
                pageNumber: _state.pageNumber,
                itemCount: _state.PAGE_ITEM_COUNT
            });

            _facade.getReceptionistMessages(options)
                .fail(promise.reject)
                .done((foundMessages) => {
                    const liveReceptionistScripts = _availableSubscriptions();

                    _state.isLastPage = foundMessages.length === 0;

                    if (_state.isLastPage) {
                        _state.availableReceptionistMessages = [];
                        setReceptionistMessages();

                        _facade.checkAnyReceptionistMessages()
                            .fail(promise.reject)
                            .done(anyMessages => {
                                anyReceptionistMessages(anyMessages);
                                promise.resolve();
                            });
                    }
                    else {
                        const displayMessages = foundMessages.map((message) => {
                            return prepareReceptionistMessageForDisplay(message, liveReceptionistScripts);
                        });
                        _state.availableReceptionistMessages = searchFilterReceptionistMessages(searchText, displayMessages);

                        if (_state.availableReceptionistMessages.length < MINIMUM_ITEM_COUNT) {
                            getNextReceptionistMessagesFromDatabase()
                                .fail(promise.reject)
                                .done(() => {
                                    setReceptionistMessages();
                                    promise.resolve();
                                });
                        } else {
                            setReceptionistMessages();
                            promise.resolve();
                        }
                    }
                });
        });
    };

    const setReceptionistMessages = () => {
        setTimeout(() => {
            _state.numberOfReceptionistMessagesShown = _state.ITEMS_PER_PAGE_DISPLAYED;
            receptionistMessages(_state.availableReceptionistMessages.slice(0, _state.numberOfReceptionistMessagesShown));
            isDataLoading(false);
            if (_receptionistMessageContentPaneScrollEventId === null){
                _receptionistMessageContentPaneScrollEventId = _eventManager.subscribeBottomScrollBufferReached(getNextDataGridEvent);
            }
        }, spinEndTime - new Date().getTime());
    };

    const getNextReceptionistMessages = (eventSource) => {
        if (eventSource !== scrollEventSource) {
            return;
        }

        return _promiseFactory.defer((promise) => {
            if (sidebar && _state.isLastPage === false) {
                const additionalReceptionistMessagesShown = _state.numberOfReceptionistMessagesShown + _state.ITEMS_PER_PAGE_DISPLAYED;
                const nextAvailableReceptionistMessages = _state.availableReceptionistMessages.slice(_state.numberOfReceptionistMessagesShown, additionalReceptionistMessagesShown);
                receptionistMessages.push.apply(receptionistMessages, nextAvailableReceptionistMessages);
                _state.numberOfReceptionistMessagesShown = additionalReceptionistMessagesShown;

                if (_state.numberOfReceptionistMessagesShown >= _state.availableReceptionistMessages.length) {
                    getNextReceptionistMessagesFromDatabase()
                        .fail(promise.reject)
                        .done(promise.resolve);
                } else {
                    promise.resolve();
                }
            } else {
                promise.resolve();
            }
        });
    };

    const getNextReceptionistMessagesFromDatabase = () => {
        return _promiseFactory.defer((promise) => {
            _state.pageNumber++;
            _state.numberOfReceptionistMessagesShown = _state.availableReceptionistMessages.length;

            const searchOptions = sidebar.getSearchOptions();
            const { searchText } = searchOptions;
            const options = Object.assign({}, searchOptions, {
                pageNumber: _state.pageNumber,
                itemCount: _state.PAGE_ITEM_COUNT
            });

            _facade.getReceptionistMessages(options)
                .fail(promise.reject)
                .done((foundMessages) => {
                    _state.isLastPage = foundMessages.length === 0;

                    const liveReceptionistScripts = _availableSubscriptions();

                    const displayMessages = foundMessages.map((message) => {
                        return prepareReceptionistMessageForDisplay(message, liveReceptionistScripts);
                    });
                    const searchFilterMessages = searchFilterReceptionistMessages(searchText, displayMessages);
                    _state.availableReceptionistMessages.push.apply(_state.availableReceptionistMessages, searchFilterMessages);

                    if (_state.availableReceptionistMessages.length < MINIMUM_ITEM_COUNT && _state.isLastPage === false) {
                        getNextReceptionistMessagesFromDatabase()
                            .fail(promise.reject)
                            .done(promise.resolve);
                    } else {
                        promise.resolve();
                    }
                });
        });
    };

    const getNextDataGridEvent = debounceEvent(getNextReceptionistMessages, 100);
    const searchTextChanged = debounceEvent(onSearchTextChanged, 750);

    const searchFilterReceptionistMessages = (searchText, availableReceptionistMessages) => {
        const receptionistMessageSearchTextFilter = new FilterConstructor();
        receptionistMessageSearchTextFilter
            .addProperty("callHistoryId")
            .addProperty("callerAni")
            .addProperty("originatorTitle")
            .addProperty("originatorMetadata")
            .addProperty("liveReceptionistScriptName")
            .addProperty("hostedNumber")
            .addProperty("hostedPhoneNumberFormatted")
            .addProperty("liveReceptionistScriptMetadata")
            .addProperty("messageLeftDateTimeDisplay")
            .addProperty("status")
            .addProperty("junkStatus")
            .addValue(searchText);
        return receptionistMessageSearchTextFilter.filter(availableReceptionistMessages);
    };

    const toggleReceptionistMessageStatus = (receptionistMessage) => {
        return _promiseFactory.defer((promise) => {
            const currentStatus = receptionistMessage.status();

            let newStatus;
            if (currentStatus ===  _receptionistMessageConstants.status.new.name) {
                newStatus = _receptionistMessageConstants.status.reviewed.name;
            } else {
                newStatus = _receptionistMessageConstants.status.new.name;
            }

            setReceptionistMessageStatus(receptionistMessage, newStatus)
                .fail(promise.reject)
                .done(promise.resolve);
        });
    };

    const navigateToMessage = (receptionistMessage) => {
        const receptionistMessageUrl = `${_navigationConfiguration.routesById.receptionistMessage.baseUrl}/${receptionistMessage.receptionistMessageId}`;
        _windowControl.openNewWindow(receptionistMessageUrl, "_blank");
    };

    const deleteReceptionistMessage = (receptionistMessage) => {
        return _promiseFactory.deferIndefinitely((promise) => {
            let receptionistMessageId = receptionistMessage.receptionistMessageId;
            _facade.deleteReceptionistMessage(receptionistMessageId)
                .fail(promise.reject)
                .done(() => {
                    removeReceptionistMessageFromUi(receptionistMessageId);
                    promise.resolve();
                });
        });
    };

    const buildExpandedCallDetailItem = (messageItem) => {
        return _promiseFactory.defer((deferredObject) => {
            _facade.getCallDetails(messageItem.receptionistMessageId, messageItem.callHistoryId)
                .fail(deferredObject.reject)
                .done((callDetails) => {
                    messageItem.callDetails(callDetails);
                    messageItem.isReady(true);
                    messageItem.isStatusChanging(false);
                    deferredObject.resolve(messageItem);
                });
        });
    };

    const expandCallDetails = (receptionistMessage) => {
        return _promiseFactory.deferIndefinitely((promise) => {
            receptionistMessage.isExpanded(!receptionistMessage.isExpanded());
            receptionistMessage.isStatusChanging(true);

            if (receptionistMessage.callDetails() === null) {
                buildExpandedCallDetailItem(receptionistMessage);
            }

            if (receptionistMessage.isExpanded() === true) {
                setReceptionistMessageStatus(receptionistMessage, _receptionistMessageConstants.status.reviewed.name)
                    .fail(promise.reject)
                    .done(promise.resolve);
            } else {
                promise.resolve();
            }
        });
    };

    const showMessage = (elem) => {
        if (!isListInitialized) {
            return;
        }

        if (elem.nodeType === 1) {
            $(elem).hide().slideDown().fadeIn("normal");
        }
    };

    const renderComplete = () => {
        isListInitialized = true;
    };

    const _initializeScreen = () => {
        if (isScreenAlreadyInitialized) {
            return _promiseFactory.wait();
        }
        isScreenAlreadyInitialized = true;

        _navigationConfiguration = require('settings/navigationConfiguration');
        _windowControl = require('presentation/common/window/windowControl');

        const FacadeConstructor = require('presentation/receptionist/facades/receptionistFacade');
        _facade = new FacadeConstructor();
        _facade.init(_promiseFactory);

        _facade.onReceptionistMessageAdded(onReceptionistMessageAdded);
        _facade.onReceptionistMessageDeleted(onReceptionistMessageDeleted);
        _facade.onReceptionistMessageStatusChanged(onReceptionistMessageStatusChanged);
        _facade.onLiveReceptionistScriptStatusChanged(onLiveReceptionistScriptStatusChanged);
        _facade.onLiveReceptionistScriptSubscriberSettingsChanged(onSubscriberSettingsChanged);

        refreshMessageListing();
        return _promiseFactory.wait();
    };

    const _detachScreen = () => {
        isScreenAlreadyInitialized = false;
        _eventManager.unsubscribe(_receptionistMessageContentPaneScrollEventId);
        _receptionistMessageContentPaneScrollEventId = null;
        _facade.dispose();
    };

    const setSidebar = (mySidebar) => {
        sidebar = mySidebar;
    };

    return {
        refreshMessageListing,
        searchTextChanged,
        deleteReceptionistMessage,
        toggleReceptionistMessageStatus,
        showMessage,
        expandCallDetails,
        navigateToMessage,
        renderComplete,

        isDataLoading,
        showEmptyState,

        emptyStateContentKey,
        receptionistMessages,

        setSidebar,

        parentViewModel,
        scrollEventSource,

        // Durandal events:
        activate: _initializeScreen,
        detached: _detachScreen,
    };
});
